import { useEffect, useLayoutEffect, useState, type RefObject } from "react";

/*
 * The frozen bands (owner, 2026-09-21, every product — gairm/lms#215;
 * crmarr/templates frozen-page-banner-and-back-to-top.md): the page's title
 * and its filters stay in view while a long list scrolls, and a table's
 * column titles stay under them. Each band is `position: sticky`; the
 * bands below need to know how tall the ones above are, so each publishes
 * its height as a CSS variable:
 *
 *   --shell-bar   the shell's row with the round ? (AppLayout), on the root — always
 *                 sticky while signed in, so help is in reach on every page
 *                 however far it scrolls (Claude's walk of 2026-09-23;
 *                 gairm/lms#220); 0 when signed out
 *   --page-bar    the PageHeader (eyebrow, title, subtitle, actions, toolbar), on the root
 *   --table-bar   a DataTable's filter row, on the table's own wrapper
 *
 * The page banner sticks under the shell's row, at `--shell-bar`; a
 * table's filter row at `--shell-bar + --page-bar`, and its column titles
 * at `--shell-bar + --page-bar + --table-bar`. The
 * document is what scrolls here (the rail is sticky beside it, as in
 * Stugum's port), so the scroll root is the document element unless a
 * layout marks an ancestor `data-scroll-root`.
 *
 * Whether a band freezes is measured, not declared (useFrozenBand): off on
 * a phone or a short window, and off wherever the band is too tall for the
 * window it is in. A band publishes its height only while it is frozen, so
 * one that scrolls away never leaves the titles below it hanging under an
 * empty gap. The column titles, a single short row, use the `freeze:`
 * variant (index.css) directly.
 */

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/** The nearest scrolling ancestor a layout marks, else the document element. */
export function scrollRootOf(el: Element | null): HTMLElement {
  return (el?.closest("[data-scroll-root]") as HTMLElement | null) ?? document.documentElement;
}

/** The `freeze:` variant's media query (index.css) — keep the two in step. */
export const FREEZE_QUERY = "(min-width: 48rem) and (min-height: 30rem)";

/**
 * The most of the window a frozen band may take. A band taller than this
 * does not freeze at all: the audit trail's filters wrap to three rows below
 * `lg` and at x-large text, and a band of 500 px left a sliver of rows — or
 * none — for whatever focus moves to (WCAG 2.4.11; review of gairm/lms#239).
 */
export const MAX_BAND_SHARE = 1 / 3;

/**
 * A frozen band: returns whether it may freeze now, and publishes its
 * rendered height as `--<name>` on the scroll root ('root') or its parent
 * ('parent') while it does — `0px` while it does not, so a band that
 * scrolls away leaves nothing hanging under a gap. It freezes when the
 * window has room (FREEZE_QUERY) and the band, measured, is no taller than
 * `MAX_BAND_SHARE` of the window. Re-measured when the band resizes (a
 * wrapped title, the text-size preference), when the window does, and when
 * the media query flips. Zero again on unmount.
 */
export function useFrozenBand(ref: RefObject<HTMLElement | null>, name: string, target: "root" | "parent" = "root", enabled = true): boolean {
  const [frozen, setFrozen] = useState(false);
  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const host = target === "root" ? scrollRootOf(el) : (el.parentElement ?? el);
    const mq = typeof window.matchMedia === "function" ? window.matchMedia(FREEZE_QUERY) : null;
    const publish = () => {
      const height = el.getBoundingClientRect().height;
      const fits = enabled && (mq?.matches ?? false) && height > 0 && height <= window.innerHeight * MAX_BAND_SHARE;
      setFrozen(fits);
      host.style.setProperty(`--${name}`, fits ? `${height}px` : "0px");
    };
    publish();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    ro?.observe(el);
    window.addEventListener("resize", publish);
    mq?.addEventListener("change", publish);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", publish);
      mq?.removeEventListener("change", publish);
      host.style.setProperty(`--${name}`, "0px");
    };
  }, [ref, name, target, enabled]);
  return frozen;
}

/**
 * Publishes an element's rendered height as `--<name>` on the scroll root
 * for as long as it is mounted and `enabled`, `0px` otherwise: the
 * shell's help row, which always sticks, so the bands below stick under it.
 */
export function useHeightVar(ref: RefObject<HTMLElement | null>, name: string, enabled = true): void {
  useIsoLayoutEffect(() => {
    const el = ref.current;
    const host = scrollRootOf(el);
    if (!el || !enabled) {
      host.style.setProperty(`--${name}`, "0px");
      return;
    }
    const publish = () => host.style.setProperty(`--${name}`, `${el.getBoundingClientRect().height}px`);
    publish();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    ro?.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", publish);
      host.style.setProperty(`--${name}`, "0px");
    };
  }, [ref, name, enabled]);
}

/**
 * True once a sticky element is actually stuck: its top edge sits at its
 * sticky offset (under the shell's help row, for the page banner) while
 * the root has scrolled. Read on scroll events
 * (passive) rather than from a sentinel element, which a page's gaps would
 * push about. Draws the band's bottom edge only while rows pass beneath it.
 */
export function useStuck(ref: RefObject<HTMLElement | null>): boolean {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = scrollRootOf(el);
    const doc = root === document.documentElement;
    const scroller: HTMLElement | Window = doc ? window : root;
    const check = () => {
      const scrolled = doc ? window.scrollY : root.scrollTop;
      if (scrolled <= 0 || getComputedStyle(el).position !== "sticky") {
        setStuck(false);
        return;
      }
      const rootTop = doc ? 0 : root.getBoundingClientRect().top;
      const offset = Number.parseFloat(getComputedStyle(el).top) || 0;
      setStuck(el.getBoundingClientRect().top <= rootTop + offset + 1);
    };
    check();
    scroller.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      scroller.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [ref]);
  return stuck;
}
